"use client";
import React, { useState, useRef } from "react";
import {
    ZipIcon,
    TxtIcon,
    PdfIcon,
    AudioIcon,
    FileIcon,
    WebsiteIcon,
    PlayIcon,
    PauseIcon,
    PreviewIcon,
    ExternalLinkIcon,
    DownloadIcon
} from "./FileIcons";
import "./styles.scss";

interface Deliverable {
    id: string;
    name: string;
    websiteUrl?: string;
    file?: {
        id: string;
        url: string;
        filename: string;
        type: string;
        size: number;
    }[];
    type: string;
    file_url?: string;
}

interface DeliverableCardProps {
    deliverable: Deliverable;
}

export default function DeliverableCard({ deliverable }: DeliverableCardProps) {
    const [isPlaying, setIsPlaying] = useState(false);
    const audioRef = useRef<HTMLAudioElement>(null);

    // 获取文件类型和图标组件
    const getFileTypeAndIcon = () => {
        if (deliverable.websiteUrl) {
            return {
                type: 'website',
                IconComponent: WebsiteIcon,
                extension: ''
            };
        }

        // 优先使用 file_url
        if (deliverable.file_url) {
            const extension = deliverable.file_url.split('.').pop()?.toLowerCase() || '';

            switch (extension) {
                case 'zip':
                case 'rar':
                case '7z':
                    return {
                        type: 'archive',
                        IconComponent: ZipIcon,
                        extension: extension
                    };
                case 'txt':
                    return {
                        type: 'text',
                        IconComponent: TxtIcon,
                        extension: extension
                    };
                case 'pdf':
                    return {
                        type: 'pdf',
                        IconComponent: PdfIcon,
                        extension: extension
                    };
                case 'mp3':
                case 'wav':
                case 'ogg':
                case 'flac':
                case 'aac':
                    return {
                        type: 'audio',
                        IconComponent: AudioIcon,
                        extension: extension
                    };
                default:
                    return {
                        type: 'file',
                        IconComponent: FileIcon,
                        extension: extension
                    };
            }
        }

        // 回退到原有的 file 字段处理
        if (deliverable.file && deliverable.file.length > 0) {
            const file = deliverable.file[0];
            const extension = file.filename.split('.').pop()?.toLowerCase() || '';

            switch (extension) {
                case 'zip':
                case 'rar':
                case '7z':
                    return {
                        type: 'archive',
                        IconComponent: ZipIcon,
                        extension: extension
                    };
                case 'txt':
                    return {
                        type: 'text',
                        IconComponent: TxtIcon,
                        extension: extension
                    };
                case 'pdf':
                    return {
                        type: 'pdf',
                        IconComponent: PdfIcon,
                        extension: extension
                    };
                case 'mp3':
                case 'wav':
                case 'ogg':
                case 'flac':
                case 'aac':
                    return {
                        type: 'audio',
                        IconComponent: AudioIcon,
                        extension: extension
                    };
                default:
                    return {
                        type: 'file',
                        IconComponent: FileIcon,
                        extension: extension
                    };
            }
        }

        return {
            type: 'unknown',
            IconComponent: FileIcon,
            extension: ''
        };
    };

    // 处理点击事件
    const handleClick = () => {
        const { type } = getFileTypeAndIcon();

        if (type === 'website' && deliverable.websiteUrl) {
            window.open(deliverable.websiteUrl, '_blank');
        } else if (type === 'audio') {
            handleAudioPlay();
        } else if (deliverable.file_url) {
            window.open(deliverable.file_url, '_blank');
        } else if (deliverable.file && deliverable.file[0]) {
            handlePreview();
        }
    };

    // 处理下载
    const handleDownload = async (e: React.MouseEvent) => {
        e.stopPropagation(); // 阻止事件冒泡

        const downloadFile = async (url: string, filename: string) => {
            try {
                console.log('开始下载文件:', { url, filename });

                // 使用后端API进行下载
                const downloadUrl = `/api/download?url=${encodeURIComponent(url)}`;
                console.log('使用下载API:', downloadUrl);

                // 创建一个隐藏的a标签来触发下载
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.download = filename; // 这可能不会生效，因为文件名由服务器端的Content-Disposition决定
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } catch (error) {
                console.error('下载失败:', error);
                // 对于音频文件，即使下载失败也不要在新窗口打开
                if (type !== 'audio') {
                    window.open(url, '_blank');
                }
            }
        };

        const fileUrl = deliverable.file_url;
        const fileInfo = deliverable.file && deliverable.file[0];

        console.log('准备下载文件:', {
            fileUrl,
            fileInfo,
            type: getFileTypeAndIcon().type
        });

        if (fileUrl) {
            // 从 file_url 中提取文件名
            const filename = fileUrl.split('/').pop() || 'download';
            await downloadFile(fileUrl, filename);
        } else if (fileInfo) {
            await downloadFile(fileInfo.url, fileInfo.filename);
        }
    };

    // 处理预览
    const handlePreview = () => {
        if (deliverable.file_url) {
            window.open(deliverable.file_url, '_blank');
        } else if (deliverable.file && deliverable.file[0]) {
            window.open(deliverable.file[0].url, '_blank');
        }
    };

    // 处理音频播放
    const handleAudioPlay = () => {
        if (audioRef.current) {
            if (isPlaying) {
                audioRef.current.pause();
                setIsPlaying(false);
            } else {
                audioRef.current.play();
                setIsPlaying(true);
            }
        }
    };

    // 格式化文件大小
    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i)) + sizes[i];
    };

    const { type, IconComponent, extension } = getFileTypeAndIcon();
    const fileSize = deliverable.file && deliverable.file[0] ? deliverable.file[0].size : 0;

    // 获取显示的文件信息（大小和类型）
    const getFileInfo = () => {
        if (type === 'website') {
            return 'link';
        }

        let fileType = '';
        if (deliverable.file_url) {
            fileType = deliverable.file_url.split('.').pop()?.toLowerCase() || '';
        } else if (deliverable.file && deliverable.file[0]) {
            fileType = deliverable.file[0].filename.split('.').pop()?.toLowerCase() || '';
        }

        if (fileSize > 0) {
            return `${formatFileSize(fileSize)}·${fileType}`;
        }

        return fileType ? `·${fileType}` : '';
    };

    return (
        <div className="deliverable-card" onClick={handleClick}>
            <div className="deliverable-card-icon">
                <IconComponent size={28} fileExtension={extension} />
            </div>

            <div className="deliverable-card-content">
                <div
                    className="deliverable-card-name"
                    title={deliverable.name}
                >
                    {deliverable.name}
                </div>
                <div className="deliverable-card-size">
                    {getFileInfo()}
                </div>
            </div>

            <div className="deliverable-card-actions">
                {/* 预览按钮 - 除了ZIP、音频和网站以外都显示 */}
                {type !== 'archive' && type !== 'website' && type !== 'audio' && (
                    <button
                        className="deliverable-action-btn preview"
                        onClick={(e) => {
                            e.stopPropagation();
                            handlePreview();
                        }}
                        title="预览"
                    >
                        <PreviewIcon size={16} />
                    </button>
                )}

                {/* 播放/暂停按钮 - 只有音频显示 */}
                {type === 'audio' && (
                    <button
                        className="deliverable-action-btn play"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleAudioPlay();
                        }}
                        title={isPlaying ? "暂停" : "播放"}
                    >
                        {isPlaying ? <PauseIcon size={16} /> : <PlayIcon size={16} />}
                    </button>
                )}

                {/* 下载按钮 - 除了网站以外都显示 */}
                {type !== 'website' && (
                    <button
                        className="deliverable-action-btn download"
                        onClick={handleDownload}
                        title="下载"
                    >
                        <DownloadIcon size={16} />
                    </button>
                )}

                {/* 跳转按钮 - 只有网站显示 */}
                {type === 'website' && (
                    <button
                        className="deliverable-action-btn external"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (deliverable.websiteUrl) {
                                window.open(deliverable.websiteUrl, '_blank');
                            }
                        }}
                        title="打开链接"
                    >
                        <ExternalLinkIcon size={16} />
                    </button>
                )}
            </div>

            {/* 音频元素 */}
            {type === 'audio' && (
                <audio
                    ref={audioRef}
                    src={deliverable.file_url || (deliverable.file && deliverable.file[0]?.url)}
                    onEnded={() => setIsPlaying(false)}
                    style={{ display: 'none' }}
                />
            )}
        </div>
    );
} 